import axios from 'axios'
import Swal from 'sweetalert2'
import Pagination from '../../utils/ResultViewer/Pagination'
import { ResultProps } from '../../utils/ResultViewer/ResultViewer'
import Table from '../../utils/ResultViewer/Table'
import CompanyTasksFilters from './CompanyTasksFilters'
import { getDefaultTaskFilterParams, TASK_STATUS_FILTER_PARAM } from './taskFilterDefaults'

export type CompanyTasksProps = ResultProps & {
  commentCallback: (id: any, refresh: Function) => void
  attachmentCallback: (id: any, refresh: Function) => void
  exportLabel?: string
  exportFailedMessage?: string
}

type CompanyTasksState = CompanyTasksProps & {
  commentsCount?: Array<{ task: any; comment_count: number }>
  exporting?: boolean
}

class CompanyTasksTable extends Table<CompanyTasksProps> {
  public state: CompanyTasksState

  constructor(props: CompanyTasksProps) {
    super(props)
    const exportAction = {
      text: props.exportLabel || 'Export to Excel',
      class: 'btn btn-sm btn-light-primary me-2',
      icon: 'fas fa-file-excel',
      onClick: (_event: any, _refresh: Function) => {
        this.exportToExcel()
      },
    }
    this.state = {
      title: props.title,
      actionItems: [exportAction, ...(props.actionItems || [])],
      dataUrl: props.dataUrl,
      data: {
        headers: {},
        data: [],
        search: [],
        pagination: {
          limit: 100,
          page: 1,
          total_count: 0,
        },
      },
      page: 1,
      orderBy: '',
      orderDirection: '',
      editCallback: props.editCallback,
      deleteCallback: props.deleteCallback,
      filterParams: getDefaultTaskFilterParams(),
      commentCallback: props.commentCallback,
      attachmentCallback: props.attachmentCallback,
      exportLabel: props.exportLabel,
      exportFailedMessage: props.exportFailedMessage,
      commentsCount: undefined,
      exporting: false,
    }
  }

  componentDidMount(): void {
    axios.get('/tasks/getTaskCommentCount').then((response) => {
      this.setState({
        commentsCount: response.data.data,
      })
    })
    this.fetchData()
  }

  setFilterParams(name: string, value: any) {
    const filterParams = { ...(this.state.filterParams as any) }
    if (name === TASK_STATUS_FILTER_PARAM && Array.isArray(value) && value.length === 0) {
      delete filterParams[name]
    } else {
      filterParams[name] = value
    }
    this.setState(
      {
        filterParams,
        page: 1,
      },
      () => {
        this.fetchData()
      }
    )
  }

  clearFilterParams() {
    this.setState(
      {
        filterParams: getDefaultTaskFilterParams(),
        page: 1,
      },
      () => {
        this.fetchData()
      }
    )
  }

  private getExportErrorMessage(payload: any, fallback: string): string {
    return (
      payload?.messages?.[0]?.[0] ||
      payload?.messages?.['0']?.[0] ||
      payload?.messages?.error?.[0] ||
      fallback
    )
  }

  public async exportToExcel(): Promise<void> {
    if (this.state.exporting) {
      return
    }

    this.setState({ exporting: true })
    const fallbackMessage = this.props.exportFailedMessage || 'Export failed. Please try again.'

    try {
      const response = await axios.get('/tasks/export', {
        responseType: 'blob',
        params: {
          page: 1,
          orderBy: this.state.orderBy,
          orderDirection: this.state.orderDirection,
          ...this.state.filterParams,
        },
      })

      const contentType = (response.headers as any)['content-type'] || ''
      if (contentType.includes('application/json')) {
        const errorText = await (response.data as Blob).text()
        let message = fallbackMessage
        try {
          message = this.getExportErrorMessage(JSON.parse(errorText), fallbackMessage)
        } catch (_) {
          // keep default message
        }
        Swal.fire('Error', message, 'error')
        return
      }

      const disposition = (response.headers as any)['content-disposition'] || ''
      let filename = `task_management_${new Date().toISOString().split('T')[0]}.xlsx`
      const match = /filename="?([^";]+)"?/i.exec(disposition)
      if (match?.[1]) {
        filename = match[1]
      }

      const blob = new Blob([response.data], {
        type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error: any) {
      let message = fallbackMessage
      const data = error?.response?.data
      if (data instanceof Blob) {
        try {
          message = this.getExportErrorMessage(JSON.parse(await data.text()), fallbackMessage)
        } catch (_) {
          // keep default message
        }
      }
      Swal.fire('Error', message, 'error')
    } finally {
      this.setState({ exporting: false })
    }
  }

  render() {
    let limit = this.state.data?.pagination.limit ?? 0
    let page = this.state.page ?? 0
    let totalCount = this.state.data?.pagination.total_count ?? 0
    let viewStart = (page - 1) * limit
    if (viewStart === 0) {
      viewStart = 1
    } else {
      viewStart = viewStart + 1
    }

    let viewEnd = page * limit
    if (viewEnd > totalCount) {
      viewEnd = totalCount
    }
    if (!this.state.data) {
      return <>Loading...</>
    }

    return (
      <div className='card mb-5 mb-xl-8'>
        <div className='card-header'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bold fs-3 mb-1'>{this.props.title}</span>
            <span className='text-muted mt-1 fw-semibold fs-7'>
              Out of {this.state.data?.pagination.total_count ?? 0} records {viewStart} -&nbsp;
              {viewEnd ?? 1} is monitoring.
            </span>
          </h3>
          <div className='align-self-end align-self-center'>
            <CompanyTasksFilters
              search={this.state.data.search ?? []}
              filterParams={(this.state.filterParams as any) || {}}
              setFilterParams={(name: string, value: any) => {
                this.setFilterParams(name, value)
              }}
              applyFilter={() => {
                this.fetchData()
              }}
              clearFilters={() => {
                this.clearFilterParams()
              }}
            />
            {this.state.actionItems?.map((item, index) => {
              return (
                <button
                  key={index}
                  className={item.class}
                  onClick={(event) => {
                    item.onClick(event, this.fetchData)
                  }}
                >
                  <span className={item.icon + ' me-2'}></span>
                  {item.text}
                </button>
              )
            })}
          </div>
        </div>
        <div className='card-body overflow-auto'>
          <table className='table align-middle table-row-dashed fs-6 gy-5 dataTable no-footer'>
            <thead>
              <tr>{this.renderHeaders()}</tr>
            </thead>
            <tbody>{this.renderRows()}</tbody>
          </table>
        </div>
        <div className='card-footer'>
          <Pagination {...this.state.data.pagination} setPage={this.setPage} />
        </div>
      </div>
    )
  }

  renderRow(row: any, key: string, colIndex: number): JSX.Element {
    if (key === 'status') {
      let badgeClass = 'badge-success'
      let statusLabel = row[key]
      let icon = 'fa-check'
      switch (row[key]) {
        case 'in_progress':
          badgeClass = 'badge-primary'
          statusLabel = 'In Progress'
          icon = 'fa-play'
          break
        case 'pending':
          badgeClass = 'badge-warning'
          statusLabel = 'Pending'
          icon = 'fa-clock'
          break
        case 'completed':
          badgeClass = 'badge-success'
          statusLabel = 'Completed'
          icon = 'fa-check'
          break
        case 'closed':
          badgeClass = 'badge-dark'
          statusLabel = 'Closed'
          icon = 'fa-times'
          break
        case 'outstanding':
          badgeClass = 'badge-danger'
          statusLabel = 'Outstanding'
          icon = 'fa-exclamation-triangle'
          break
        case 'archived':
          badgeClass = 'badge-secondary'
          statusLabel = 'Archived'
          icon = 'fa-archive'
          break
      }
      return (
        <td key={colIndex}>
          <div className={'badge ' + badgeClass}>
            <span className={'fa ' + icon} style={{ marginRight: '5px', fontSize: '12px' }}></span>
            {statusLabel}
          </div>
        </td>
      )
    } else if (key === 'edit_actions') {
      return (
        <td key={colIndex}>
          <div className='d-flex justify-content flex-shrink-0'>
            <button
              onClick={() => this.props.editCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 w-50'
            >
              TASK_{row[key]}
            </button>
            <button
              onClick={() => this.props.editCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
            >
              <span className='fa fa-edit'></span>
            </button>
            <button
              onClick={() => this.props.deleteCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1'
            >
              <span className='fa fa-trash'></span>
            </button>
            <button
              onClick={() => this.props.commentCallback(row[key], this.fetchData)}
              className='btn btn-icon btn-bg-light btn-active-color-info btn-sm me-1 position-relative'
            >
              {this.state.commentsCount &&
                this.state.commentsCount.map(
                  (item) =>
                    item.task === row[key] && (
                      <span
                        key={item.task}
                        className='position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger'
                        style={{ fontSize: '0.8em' }}
                      >
                        {item.comment_count}
                      </span>
                    )
                )}
              <span className='fa fa-comment'></span>
            </button>
          </div>
        </td>
      )
    } else {
      return super.renderRow(row, key, colIndex)
    }
  }
}

export default CompanyTasksTable
